import React from "react";
import { useTranslation } from "react-i18next";
import { useQuery } from "@apollo/client";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Typography from "@mui/material/Typography";
import { HugeiconsIcon } from "@hugeicons/react";
import { RepeatIcon } from "@hugeicons-pro/core-stroke-rounded";
import moment from "moment";
import { GET_PAYMENT_LINK_SUBSCRIPTIONS } from "graphql/subscriptions";
import RenderSubscriptionStatus from "components/common/RenderSubscriptionStatus";
import { formatCashFull } from "utils/formatCash";
import { SubscriptionStatus } from "enums/Subscription";

interface SubscriberRow {
  id: string;
  status: SubscriptionStatus;
  payerEmail: string;
  payerName?: string;
  chargeAmount: number;
  currency: string;
  nextBillingDate?: number;
  awaitingPaymentMethod?: boolean;
}

interface PaymentLinkSubscriberRowsProps {
  paymentLinkId: string;
  limit?: number;
  onViewAll: () => void;
}

const PaymentLinkSubscriberRows: React.FC<PaymentLinkSubscriberRowsProps> = ({
  paymentLinkId,
  limit = 5,
  onViewAll,
}) => {
  const { t } = useTranslation();

  const { data, loading } = useQuery(GET_PAYMENT_LINK_SUBSCRIPTIONS, {
    variables: { paymentLinkId, first: limit },
    fetchPolicy: "network-only",
  });

  const subscribers: SubscriberRow[] =
    data?.paymentLinkSubscriptions?.edges?.map(
      (edge: { node: SubscriberRow }) => edge.node,
    ) || [];

  if (!loading && !subscribers.length) {
    return (
      <Typography variant="body2" color="var(--color-text-tertiary)">
        {t("No subscribers yet")}
      </Typography>
    );
  }

  return (
    <Box
      sx={{ display: "flex", flexDirection: "column", gap: "var(--space-1)" }}
    >
      {subscribers.map((subscriber) => (
        <Box
          key={subscriber.id}
          sx={{
            display: "flex",
            alignItems: "center",
            gap: "var(--space-3)",
            paddingY: "var(--space-2)",
          }}
        >
          <HugeiconsIcon icon={RepeatIcon} size={20} />

          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography variant="subtitle2" noWrap>
              {subscriber.payerName || subscriber.payerEmail}
            </Typography>
            <Typography variant="caption" color="var(--color-text-tertiary)">
              {subscriber.nextBillingDate
                ? t("NextBillingOn", {
                    date: moment(subscriber.nextBillingDate).format("ll"),
                  })
                : t("No payment scheduled")}
            </Typography>
          </Box>

          <Typography variant="subtitle2">
            {formatCashFull(subscriber.chargeAmount, subscriber.currency)}
          </Typography>

          <RenderSubscriptionStatus
            status={subscriber.status}
            awaitingPaymentMethod={subscriber.awaitingPaymentMethod}
          />
        </Box>
      ))}

      <Button
        variant="default-soft"
        fullWidth
        onClick={onViewAll}
        sx={{ marginTop: "var(--space-2)" }}
      >
        {t("View all subscribers")}
      </Button>
    </Box>
  );
};

export default PaymentLinkSubscriberRows;
